import React from 'react';
import { cn } from '@/lib/utils';

interface NeomorphicCardProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'highlighted' | 'success' | 'warning';
  borderAccent?: 'blue' | 'green' | 'purple' | 'orange' | 'none';
  interactive?: boolean;
  onClick?: () => void;
}

/**
 * NeomorphicCard Component
 *
 * Standard neomorphic card design following brand guidelines
 * - No gradients on backgrounds (solid white or subtle gray)
 * - Consistent shadow patterns for depth
 * - Optional colored left border accent
 * - Interactive hover states
 *
 * Usage:
 * <NeomorphicCard variant="highlighted" borderAccent="blue">
 *   Content here
 * </NeomorphicCard>
 */
export const NeomorphicCard: React.FC<NeomorphicCardProps> = ({
  children,
  className,
  variant = 'default',
  borderAccent = 'none',
  interactive = false,
  onClick,
}) => {
  const baseStyles = 'relative bg-white rounded-3xl p-6 transition-all duration-300';

  const shadowStyles = {
    default: 'shadow-[inset_0_1px_0_rgba(255,255,255,0.6),inset_0_-1px_0_rgba(0,0,0,0.05),0_10px_20px_rgba(0,0,0,0.04),0_20px_40px_rgba(0,0,0,0.02)]',
    highlighted: 'shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_10px_20px_rgba(0,0,0,0.06),0_20px_40px_rgba(0,0,0,0.03)]',
    success: 'shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_10px_20px_rgba(34,197,94,0.1),0_20px_40px_rgba(34,197,94,0.05)]',
    warning: 'shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_10px_20px_rgba(251,146,60,0.1),0_20px_40px_rgba(251,146,60,0.05)]',
  };

  const borderStyles = {
    blue: 'border-l-4 border-blue-400',
    green: 'border-l-4 border-green-400',
    purple: 'border-l-4 border-purple-400',
    orange: 'border-l-4 border-orange-400',
    none: 'border border-gray-100',
  };

  const interactiveStyles = interactive
    ? 'cursor-pointer hover:scale-[1.02] hover:shadow-[0_12px_24px_rgba(0,0,0,0.08),0_24px_48px_rgba(0,0,0,0.04)]'
    : '';

  return (
    <div
      className={cn(
        baseStyles,
        shadowStyles[variant],
        borderStyles[borderAccent],
        interactiveStyles,
        className
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

/**
 * NeomorphicButton Component
 *
 * Button with neomorphic styling
 * - Solid colors (no gradients)
 * - Consistent shadow patterns
 * - Proper hover states
 */
interface NeomorphicButtonProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

export const NeomorphicButton: React.FC<NeomorphicButtonProps> = ({
  children,
  className,
  variant = 'primary',
  size = 'md',
  onClick,
  disabled = false,
  type = 'button',
}) => {
  const baseStyles = 'rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';

  const variantStyles = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 shadow-[8px_8px_16px_rgba(0,0,0,0.1),-8px_-8px_16px_rgba(255,255,255,0.9)] hover:shadow-[4px_4px_8px_rgba(0,0,0,0.1),-4px_-4px_8px_rgba(255,255,255,0.9)]',
    secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 shadow-[8px_8px_16px_rgba(0,0,0,0.1),-8px_-8px_16px_rgba(255,255,255,0.9)] hover:shadow-[4px_4px_8px_rgba(0,0,0,0.1),-4px_-4px_8px_rgba(255,255,255,0.9)]',
    ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 border border-gray-200',
  };

  const sizeStyles = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        baseStyles,
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
    >
      {children}
    </button>
  );
};

/**
 * NeomorphicInput Component
 *
 * Form input with neomorphic styling
 * - Inner shadow for depth
 * - Clean white background
 * - Focus states
 * - Support for Enter/Shift+Enter handling
 */
interface NeomorphicInputProps {
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  type?: 'text' | 'number' | 'email';
  disabled?: boolean;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const NeomorphicInput: React.FC<NeomorphicInputProps> = ({
  value,
  onChange,
  placeholder,
  className,
  type = 'text',
  disabled = false,
  onKeyPress,
}) => {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyPress={onKeyPress}
      placeholder={placeholder}
      disabled={disabled}
      className={cn(
        'w-full px-4 py-3 rounded-xl bg-white border border-gray-200',
        'shadow-[inset_2px_2px_4px_rgba(0,0,0,0.1),inset_-2px_-2px_4px_rgba(255,255,255,0.9)]',
        'focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'transition-all duration-200',
        className
      )}
    />
  );
};

/**
 * NeomorphicTextarea Component
 *
 * Textarea with neomorphic styling for multi-line input
 * - Inner shadow for depth
 * - Clean white background
 * - Focus states
 * - Support for Enter/Shift+Enter handling
 */
interface NeomorphicTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  onKeyPress?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  rows?: number;
}

export const NeomorphicTextarea: React.FC<NeomorphicTextareaProps> = ({
  value,
  onChange,
  placeholder,
  className,
  disabled = false,
  onKeyPress,
  rows = 3,
}) => {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyPress={onKeyPress}
      placeholder={placeholder}
      disabled={disabled}
      rows={rows}
      className={cn(
        'w-full px-4 py-3 rounded-xl bg-white border border-gray-200',
        'shadow-[inset_2px_2px_4px_rgba(0,0,0,0.1),inset_-2px_-2px_4px_rgba(255,255,255,0.9)]',
        'focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'transition-all duration-200 resize-none',
        className
      )}
    />
  );
};

export default NeomorphicCard;